/* MAD Revamp — tiny progressive enhancements (no dependencies). */
(() => {
  const root = document.documentElement;

  /* ---- Reveal on scroll (content is visible by default; failsafe after 2.5s) ---- */
  const initReveal = (scope = document) => {
    const els = scope.querySelectorAll('.mad-reveal:not(.is-in)');
    if (!('IntersectionObserver' in window) || !root.classList.contains('js-mad')) {
      els.forEach(el => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e, i) => {
        if (!e.isIntersecting) return;
        e.target.style.transitionDelay = `${Math.min(i, 4) * 60}ms`;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(el => io.observe(el));
  };
  setTimeout(() => document.querySelectorAll('.mad-reveal').forEach(el => el.classList.add('is-in')), 2500);

  /* ---- Truck finder filter ---- */
  const initFinder = (scope = document) => {
    scope.querySelectorAll('[data-mad-finder]').forEach(finder => {
      if (finder.dataset.ready) return;
      finder.dataset.ready = '1';
      const tabs = finder.querySelectorAll('[data-filter]');
      const items = finder.querySelectorAll('[data-make]');
      const empty = finder.querySelector('.mad-finder__empty');
      const apply = (key) => {
        let shown = 0;
        tabs.forEach(t => t.setAttribute('aria-pressed', String(t.dataset.filter === key)));
        items.forEach(it => {
          const makes = (it.dataset.make || '').split(' ');
          const show = key === 'all' || makes.includes(key) || makes.includes('universal');
          it.hidden = !show;
          if (show) { shown++; it.classList.add('is-in'); }
        });
        if (empty) empty.hidden = shown > 0;
        try { sessionStorage.setItem('mad-make', key); } catch (e) {}
      };
      tabs.forEach(t => t.addEventListener('click', () => apply(t.dataset.filter)));
      let saved = null;
      try { saved = sessionStorage.getItem('mad-make'); } catch (e) {}
      const params = new URLSearchParams(location.search);
      const initial = params.get('truck') || saved;
      if (initial && finder.querySelector(`[data-filter="${initial}"]`)) apply(initial);
    });
  };

  /* ---- Before / after slider ---- */
  const initBA = (scope = document) => {
    scope.querySelectorAll('.mad-ba').forEach(ba => {
      const input = ba.querySelector('input[type=range]');
      if (!input || input.dataset.ready) return;
      input.dataset.ready = '1';
      const set = v => ba.style.setProperty('--pos', v + '%');
      input.addEventListener('input', () => set(input.value));
      set(input.value);
    });
  };

  /* ---- Delivery estimate: processing 1–5 business days + transit 3–7 business days ---- */
  const addBusinessDays = (date, n) => {
    const d = new Date(date);
    while (n > 0) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) n--; }
    return d;
  };
  const initETA = (scope = document) => {
    scope.querySelectorAll('[data-mad-eta]').forEach(el => {
      const min = parseInt(el.dataset.min || '4', 10);
      const max = parseInt(el.dataset.max || '12', 10);
      const now = new Date();
      const fmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const a = addBusinessDays(now, min), b = addBusinessDays(now, max);
      const out = el.querySelector('[data-mad-eta-out]');
      if (out) out.textContent = `${fmt.format(a)} – ${fmt.format(b)}`;
      el.hidden = false;
    });
  };

  const initAll = (scope) => { initReveal(scope); initFinder(scope); initBA(scope); initETA(scope); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  /* Theme editor support */
  document.addEventListener('shopify:section:load', e => initAll(e.target));
})();

/* ==========================================================================
   Vehicle finder — Year → Make → Model, powered by product metafield custom.fitment
   ========================================================================== */
(() => {
  const KEY = 'mad-vehicle';
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const ICON_TRUCK = '<svg class="mad-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 17V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h1"/><path d="M14 9h4l4 4v3a1 1 0 0 1-1 1h-1"/><circle cx="7" cy="17.5" r="2.5"/><circle cx="17" cy="17.5" r="2.5"/><path d="M9.5 17h5"/></svg>';
  const ICON_CART = '<svg class="mad-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/></svg>';

  const loadVehicle = () => { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; } };
  const saveVehicle = v => { try { v ? localStorage.setItem(KEY, JSON.stringify(v)) : localStorage.removeItem(KEY); } catch (e) {} };
  const vehicleName = v => `${v.y} ${v.mk} ${v.md}`;

  /* ---- feed ---- */
  let feedPromise = null;
  const loadFeed = (url) => {
    if (feedPromise) return feedPromise;
    feedPromise = (async () => {
      const products = [];
      for (let page = 1; page <= 10; page++) {
        const res = await fetch(`${url}&page=${page}`, { credentials: 'same-origin' });
        if (!res.ok) break;
        const data = JSON.parse(await res.text());
        products.push(...data.items);
        if (!data.next) break;
      }
      const entries = [], universal = [];
      products.forEach((p, i) => {
        p.fits = [];
        String(p.f || '').split(/\r?\n/).forEach(line => {
          const l = line.trim();
          if (!l || /^note:/i.test(l)) return;
          const parts = l.split('|').map(s => s.trim());
          if (/^universal$/i.test(parts[0])) { universal.push(i); p.universal = true; return; }
          const yrs = (parts[0] || '').split(/[-–]/).map(n => parseInt(n, 10));
          const y1 = yrs[0], y2 = yrs[1] || yrs[0];
          if (!y1 || !parts[1] || !parts[2]) return;
          const m = parts[2].match(/^(.*?)\s*\((.+)\)\s*$/);
          const e = { y1, y2, mk: parts[1], md: m ? m[1] : parts[2], q: m ? m[2] : '', i };
          entries.push(e); p.fits.push(e);
        });
      });
      return { products, entries, universal };
    })().catch(err => { feedPromise = null; throw err; });
    return feedPromise;
  };

  const uniqSorted = (arr, desc) => [...new Set(arr)].sort((a, b) => desc ? b - a : String(a).localeCompare(String(b), undefined, { numeric: true }));
  const fill = (sel, values, placeholder, keep) => {
    sel.innerHTML = `<option value="">${esc(placeholder)}</option>` + values.map(v => `<option value="${esc(v)}">${esc(v)}</option>`).join('');
    sel.disabled = values.length === 0;
    if (keep && values.map(String).includes(String(keep))) sel.value = keep;
  };

  /* ---- results ---- */
  const card = (p, fitText) => {
    const badge = !p.a ? '<span class="mad-badge">Sold out</span>' : (p.o > 0 ? `<span class="mad-badge mad-badge--gold">${p.o}% off</span>` : '');
    const buy = p.a && p.s
      ? `<form is="product-form" method="post" action="/cart/add" accept-charset="UTF-8" enctype="multipart/form-data"><input type="hidden" name="id" value="${esc(p.v)}"><input type="hidden" name="quantity" value="1"><button type="submit" class="mad-btn" aria-label="Add ${esc(p.t)} to cart">${ICON_CART}<span>Add</span></button></form>`
      : `<a class="mad-btn mad-btn--ghost" href="${esc(p.u)}">${p.a ? 'Options' : 'Notify me'}</a>`;
    return `<article class="mad-pcard is-in">
      <a class="mad-pcard__media" href="${esc(p.u)}" tabindex="-1" aria-hidden="true">${p.i ? `<img src="${esc(p.i)}" alt="" loading="lazy">` : ''}<span class="mad-pcard__badges">${badge}</span></a>
      <div class="mad-pcard__body">
        <div class="mad-pcard__fit">${ICON_TRUCK}<span>${esc(fitText)}</span></div>
        <h3 class="mad-pcard__title"><a href="${esc(p.u)}">${esc(p.t)}</a></h3>
        <div class="mad-pcard__foot"><div class="mad-price">${p.pv ? '<span class="mad-muted" style="font-size:13px">From</span>' : ''}<span class="mad-price__now">${esc(p.p)}</span>${p.c ? `<span class="mad-price__was">${esc(p.c)}</span>` : ''}</div>${buy}</div>
      </div>
    </article>`;
  };

  const render = (box, feed, v) => {
    const y = parseInt(v.y, 10);
    const hits = new Map();
    feed.entries.forEach(e => {
      if (e.mk === v.mk && e.md === v.md && y >= e.y1 && y <= e.y2) {
        const quals = hits.get(e.i) || [];
        if (e.q) quals.push(e.q);
        hits.set(e.i, quals);
      }
    });
    const direct = [...hits.entries()].map(([i, q]) => ({ p: feed.products[i], q: [...new Set(q)] }));
    const uni = feed.universal.filter(i => !hits.has(i)).map(i => feed.products[i]);
    const name = vehicleName(v);
    let html = `<div class="mad-vfr__head">
        <div><p class="mad-eyebrow">Your vehicle</p><h2 class="mad-h3">${esc(name)}</h2>
        <p class="mad-muted" style="margin:6px 0 0">${direct.length ? `${direct.length} ${direct.length === 1 ? 'part fits' : 'parts fit'} your truck` : 'No truck-specific parts listed yet'}</p></div>
        <button type="button" class="mad-link" data-mad-vf-change>Change vehicle</button>
      </div>`;
    if (direct.length) {
      html += `<div class="mad-grid" style="--mad-cols-d:3;--mad-ratio:1 / 1">${direct.map(({ p, q }) => card(p, `Fits your ${v.y} ${v.md}${q.length ? ' · ' + q.join(', ') : ''}`)).join('')}</div>`;
    } else {
      html += `<div class="mad-note"><span>We don't have parts listed for the ${esc(name)} yet. <a href="/pages/contact">Tell us what you're looking for</a> and we'll let you know if we can make it.</span></div>`;
    }
    if (uni.length) {
      html += `<h3 class="mad-vfr__sub">Universal parts that also work</h3><div class="mad-grid" style="--mad-cols-d:3;--mad-ratio:1 / 1">${uni.map(p => card(p, p.l || 'Universal fit')).join('')}</div>`;
    }
    box.innerHTML = html;
    box.hidden = false;
    box.querySelector('[data-mad-vf-change]')?.addEventListener('click', () => {
      const f = box.parentElement.querySelector('[data-mad-vf]');
      f?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      f?.querySelector('select')?.focus({ preventScroll: true });
    });
  };

  /* ---- header bar ---- */
  const syncBar = () => {
    const v = loadVehicle();
    document.querySelectorAll('[data-mad-vbar-k]').forEach(el => { el.dataset.orig ??= el.textContent; el.textContent = v ? 'Your truck' : el.dataset.orig; });
    document.querySelectorAll('[data-mad-vbar-v]').forEach(el => { el.dataset.orig ??= el.textContent; el.textContent = v ? vehicleName(v) : el.dataset.orig; });
    document.querySelectorAll('[data-mad-vehicle-clear]').forEach(b => { b.hidden = !v; });
  };

  /* ---- picker ---- */
  const initForm = (form) => {
    if (form.dataset.ready) return;
    form.dataset.ready = '1';
    const sy = form.querySelector('[data-vf="y"]'), sm = form.querySelector('[data-vf="mk"]'), sd = form.querySelector('[data-vf="md"]');
    const go = form.querySelector('.mad-vf__go');
    const box = form.closest('.mad-container, .mad-vdialog__in')?.querySelector('[data-mad-vf-results]');

    loadFeed(form.dataset.feedUrl).then(feed => {
      const years = [];
      feed.entries.forEach(e => { for (let y = e.y1; y <= e.y2; y++) years.push(y); });
      const makesFor = y => uniqSorted(feed.entries.filter(e => y >= e.y1 && y <= e.y2).map(e => e.mk));
      const modelsFor = (y, mk) => uniqSorted(feed.entries.filter(e => e.mk === mk && y >= e.y1 && y <= e.y2).map(e => e.md));
      const update = (keepMk, keepMd) => {
        const y = parseInt(sy.value, 10);
        if (!y) { fill(sm, [], 'Select make'); fill(sd, [], 'Select model'); go.disabled = true; return; }
        fill(sm, makesFor(y), 'Select make', keepMk);
        if (sm.value) fill(sd, modelsFor(y, sm.value), 'Select model', keepMd); else fill(sd, [], 'Select model');
        go.disabled = !(sy.value && sm.value && sd.value);
      };
      fill(sy, uniqSorted(years, true), 'Select year');
      sy.addEventListener('change', () => update(sm.value, sd.value));
      sm.addEventListener('change', () => update(sm.value, sd.value));
      sd.addEventListener('change', () => { go.disabled = !(sy.value && sm.value && sd.value); });

      const params = new URLSearchParams(location.search);
      const fromUrl = box && box.hasAttribute('data-autorun') && params.get('y') ? { y: params.get('y'), mk: params.get('mk'), md: params.get('md') } : null;
      const pre = fromUrl || loadVehicle();
      if (pre) { sy.value = pre.y; update(pre.mk, pre.md); }
      if (fromUrl && box && sy.value && sm.value && sd.value) { saveVehicle(fromUrl); syncBar(); render(box, feed, fromUrl); }

      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        if (!(sy.value && sm.value && sd.value)) return;
        const v = { y: sy.value, mk: sm.value, md: sd.value };
        saveVehicle(v); syncBar();
        if (form.dataset.target === 'inline' && box) {
          render(box, feed, v);
          box.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          location.href = `${form.dataset.resultsUrl}&y=${encodeURIComponent(v.y)}&mk=${encodeURIComponent(v.mk)}&md=${encodeURIComponent(v.md)}`;
        }
      });
    }).catch(() => { form.classList.add('is-error'); });
  };

  const initDialog = () => {
    document.querySelectorAll('[data-mad-vbar-open]').forEach(btn => {
      if (btn.dataset.ready) return; btn.dataset.ready = '1';
      const dlg = btn.closest('.shopify-section')?.querySelector('[data-mad-vdialog]');
      if (!dlg) return;
      btn.addEventListener('click', () => {
        const v = loadVehicle(), f = dlg.querySelector('[data-mad-vf]');
        if (v && f) {
          const setSel = (n, val) => { const s = f.querySelector(`[data-vf="${n}"]`); if (s) { s.value = val; s.dispatchEvent(new Event('change')); } };
          setSel('y', v.y); setSel('mk', v.mk); setSel('md', v.md);
        }
        dlg.showModal ? dlg.showModal() : dlg.setAttribute('open', '');
        dlg.querySelector('select')?.focus();
      });
      dlg.querySelector('[data-mad-vdialog-close]')?.addEventListener('click', () => dlg.close());
      dlg.addEventListener('click', e => { if (e.target === dlg) dlg.close(); });
      dlg.querySelector('[data-mad-vehicle-clear]')?.addEventListener('click', () => { saveVehicle(null); syncBar(); dlg.querySelectorAll('select').forEach(s => { s.value = ''; s.dispatchEvent(new Event('change')); }); });
    });
  };

  const init = () => { document.querySelectorAll('[data-mad-vf]').forEach(initForm); initDialog(); syncBar(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();

/* ==========================================================================
   Premium layer — background video, scroll wipe, counters, magnetic buttons
   ========================================================================== */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const conn = navigator.connection || {};
  const saveData = conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');

  /* ---- Background videos: load only when on screen, SD on phones, skip on data-saver / reduced motion ---- */
  const initVideos = (scope = document) => {
    const vids = scope.querySelectorAll('video[data-mad-video]:not([data-ready])');
    if (!vids.length || reduce || saveData || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      const v = e.target;
      v._inView = e.isIntersecting;
      if (e.isIntersecting) {
        if (!v.src) {
          const small = window.innerWidth < 750 || /3g/.test(conn.effectiveType || '');
          v.src = (small ? v.dataset.sd : v.dataset.hd) || v.dataset.sd || v.dataset.hd;
        }
        const p = v.play(); if (p && p.catch) p.catch(() => {});
      } else if (!v.paused) v.pause();
    }), { threshold: 0.2 });
    vids.forEach(v => {
      v.dataset.ready = '1';
      v.addEventListener('playing', () => v.classList.add('is-playing'), { once: true });
      v.addEventListener('canplay', () => { if (v._inView && v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } });
      io.observe(v);
    });
  };

  /* ---- Scroll wipe (pinned sections + inline) ---- */
  const wipes = new Set();
  const clamp = (n) => Math.max(0, Math.min(1, n));
  const ease = t => t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    wipes.forEach(w => {
      let p;
      if (w.pin) {
        const r = w.pin.getBoundingClientRect();
        const raw = clamp(-r.top / Math.max(1, r.height - vh));
        p = clamp((raw - .08) / .8);
        w.pin.style.setProperty('--p', raw.toFixed(4));
        w.caps.forEach(li => li.classList.toggle('is-on', raw >= parseFloat(li.dataset.at || 0) && (!li.nextElementSibling || raw < parseFloat(li.nextElementSibling.dataset.at || 2))));
      } else {
        const r = w.el.getBoundingClientRect();
        /* starts as the photo enters, finishes while it's still fully on screen */
        p = clamp((vh * .78 - r.top) / (vh * .5 + r.height * .35));
      }
      w.el.style.setProperty('--p', ease(p).toFixed(4));
    });
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  const initWipes = (scope = document) => {
    scope.querySelectorAll('[data-mad-wipe]:not([data-ready])').forEach(el => {
      el.dataset.ready = '1';
      if (reduce) { el.style.setProperty('--p', '0.5'); return; }
      const pin = el.hasAttribute('data-pin') ? el.closest('[data-mad-pin]') : null;
      wipes.add({ el, pin, caps: pin ? [...pin.querySelectorAll('.mad-reveal-pin__caps li')] : [] });
    });
    if (reduce) document.querySelectorAll('.mad-reveal-pin__caps li').forEach(li => li.classList.add('is-on'));
    update();
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* ---- Count-up numbers ("20,000+", "20K+", "30-day") ---- */
  const initCounters = (scope = document) => {
    if (reduce || !('IntersectionObserver' in window)) return;
    const els = [...scope.querySelectorAll('.mad-stat b, .mad-trust__t, .mad-chip')].filter(el => !el.dataset.counted && /^\s*\d[\d,]*/.test(el.lastChild?.textContent || el.textContent));
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      const node = [...e.target.childNodes].reverse().find(n => n.nodeType === 3 && /\d/.test(n.textContent));
      if (!node) return;
      const m = node.textContent.match(/^(\s*)(\d[\d,]*)(.*)$/s);
      if (!m) return;
      const target = parseInt(m[2].replace(/,/g, ''), 10), commas = m[2].includes(',');
      if (!target || target < 10 || !/^\s*(\+|K)/i.test(m[3])) return;
      const t0 = performance.now(), dur = 1400;
      const step = (t) => {
        const k = Math.min(1, (t - t0) / dur), v = Math.round(target * (1 - Math.pow(1 - k, 3)));
        node.textContent = m[1] + (commas ? v.toLocaleString('en-US') : String(v)) + m[3];
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }), { threshold: 0.6 });
    els.forEach(el => { el.dataset.counted = '1'; io.observe(el); });
  };

  /* ---- Magnetic buttons (desktop mouse only) ---- */
  const initMagnet = () => {
    if (reduce || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.addEventListener('pointermove', e => {
      const b = e.target.closest && e.target.closest('.mad-btn');
      document.querySelectorAll('.mad-btn[data-mag]').forEach(x => { if (x !== b) { x.style.removeProperty('--mx'); x.style.removeProperty('--my'); x.removeAttribute('data-mag'); } });
      if (!b) return;
      const r = b.getBoundingClientRect();
      b.dataset.mag = '1';
      b.style.setProperty('--mx', ((e.clientX - r.left - r.width / 2) * 0.12).toFixed(1) + 'px');
      b.style.setProperty('--my', ((e.clientY - r.top - r.height / 2) * 0.22).toFixed(1) + 'px');
    }, { passive: true });
  };

  const init = (scope) => { initVideos(scope); initWipes(scope); initCounters(scope); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { init(); initMagnet(); }); else { init(); initMagnet(); }
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
